import { useCallback, useEffect, useRef, useState } from "react";

import { ENVIRONMENT_CONFIG } from "shared/globals";

import {
  lookupDreamCastSupportSnapshot,
  parseDreamCastSnapshot,
} from "./supportOperationsApi";

function createNotConnectedError() {
  const error = new Error("DreamCast is not connected for this environment.");
  error.code = "DREAMCAST_NOT_CONNECTED";
  return error;
}

function createUnavailableSnapshotError(result) {
  const error = new Error("DreamCast data is unavailable right now.");
  error.code = "DREAMCAST_SNAPSHOT_UNAVAILABLE";
  error.status = result?.status ?? null;
  error.invalidPayload = Boolean(result?.invalidPayload);
  return error;
}

function isSnapshotCandidate(value) {
  return Boolean(value?.contacts) && Boolean(value?.dependencies);
}

export function useDreamCast(iboNumber, options = {}) {
  const [snapshotState, setSnapshotState] = useState({
    iboNumber: null,
    data: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const latestRequestRef = useRef(0);

  const isRequestCurrent = useCallback((requestId) => {
    return isMountedRef.current && latestRequestRef.current === requestId;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      latestRequestRef.current += 1;
    };
  }, []);

  const load = useCallback(async () => {
    const provider =
      typeof options.dataProvider === "function" ? options.dataProvider : null;
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    if (isMountedRef.current) {
      setLoading(true);
      setError(null);
    }

    try {
      const canReadSnapshot =
        Boolean(ENVIRONMENT_CONFIG?.flags?.supportsDreamCastSnapshotRead) &&
        typeof options.makeAuthedRequest === "function";

      if (!provider && !canReadSnapshot) {
        throw createNotConnectedError();
      }

      let snapshot;

      if (provider) {
        snapshot = await provider({
          iboNumber,
          scenarioKey: options.scenarioKey,
        });
      } else if (canReadSnapshot) {
        const result = await lookupDreamCastSupportSnapshot(
          options.makeAuthedRequest,
          iboNumber
        );
        if (!result.ok) {
          throw createUnavailableSnapshotError(result);
        }
        snapshot = result.data;
      }

      if (isSnapshotCandidate(snapshot)) {
        const normalized = parseDreamCastSnapshot(snapshot, {
          allowLegacyFixture: Boolean(provider),
        });
        if (!normalized) {
          throw createUnavailableSnapshotError({ invalidPayload: true });
        }
        snapshot = normalized;
      }

      if (!isRequestCurrent(requestId)) {
        return;
      }

      setSnapshotState({ iboNumber, data: snapshot });
    } catch (requestError) {
      if (!isRequestCurrent(requestId)) {
        return;
      }

      setError(requestError);
      setSnapshotState({ iboNumber, data: null });
    } finally {
      if (!isRequestCurrent(requestId)) {
        return;
      }

      setLoading(false);
    }
  }, [
    iboNumber,
    isRequestCurrent,
    options.dataProvider,
    options.makeAuthedRequest,
    options.scenarioKey,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const hasCurrentSnapshot = snapshotState.iboNumber === iboNumber;

  return {
    data: hasCurrentSnapshot ? snapshotState.data : null,
    loading: hasCurrentSnapshot ? loading : true,
    error: hasCurrentSnapshot ? error : null,
    refetch: load,
  };
}
